import { Component, computed, DestroyRef, inject, input, LOCALE_ID, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, finalize, switchMap } from 'rxjs';
import { FilterSortingComponent } from '../../../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { FilterInput } from '../../../../../../shared/ui/shared-filters/filter-input/filter-input';
import { FilterSelectComponent } from '../../../../../../shared/ui/shared-filters/filter-select-component/filter-select-component';
import { FilterMultiSelectComponent } from '../../../../../../shared/ui/shared-filters/filter-multi-select-component/filter-multi-select-component';
import { FilterDateComponent } from '../../../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { FilterProductSearch } from '../../../../../../shared/ui/shared-filters/filter-product-search/filter-product-search';
import { LookupService } from '../../../../../../services/pharmacy/lookup.service';
import { RouteInstancesService } from '../../../../../../services/route/route-instance-service';
import {
  RouteInstanceFilters,
  DistributorAutocomplete,
  CountResult,
} from '../../../../../../pages/admin-dashboard/admin-route-instance-management/route-instances.model';
import { LookupVTO } from '../../../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { SubmitButton } from '../../../../../../shared/ui/Buttons/submit-button/submit-button';

@Component({
  selector: 'app-route-instances-filter',
  standalone: true,
  imports: [
    FormsModule,
    FilterSortingComponent,
    FilterInput,
    FilterSelectComponent,
    FilterMultiSelectComponent,
    FilterDateComponent,
    FilterProductSearch,
    SubmitButton
],
  templateUrl: './route-instances-filter.html',
  styleUrl: './route-instances-filter.scss',
})
export class RouteInstancesFilter implements OnInit {
  private locale = inject(LOCALE_ID);
  private lookupService = inject(LookupService);
  private routeInstancesService = inject(RouteInstancesService);
  private destroyRef = inject(DestroyRef);

  filters = input.required<RouteInstanceFilters>();
  applyFilters = output<RouteInstanceFilters>();
  resetFilters = output();
  toggleSortOrder = output();

  routeName = signal('');
  distributorSearchTerm = signal('');
  selectedDistributorId = signal<number | null>(null);
  distributorResults = signal<{ id: number; titleAr: string; titleEn: string }[]>([]);
  distributorLoading = signal(false);
  distributorError = signal('');
  showDistributorDropdown = signal(false);

  statusOptions = signal<{ id: string; titleEn: string; titleAr: string }[]>([]);
  selectedStatusIds = signal<string[]>([]);
  showStatusDropdown = signal(false);

  dispatchTimeOptions = signal<{ id: string; titleEn: string; titleAr: string }[]>([]);
  selectedDispatchTimeIds = signal<string[]>([]);
  showDispatchDropdown = signal(false);

  ordersCountOptions = signal<{ id: number; titleEn: string; titleAr: string }[]>([]);
  selectedOrdersCount = signal<number | null>(null);

  pharmaciesCountOptions = signal<{ id: number; titleEn: string; titleAr: string }[]>([]);
  selectedPharmaciesCount = signal<number | null>(null);

  governorates = signal<LookupVTO[]>([]);
  selectedGovernorateIds = signal<string[]>([]);
  showGovernorateDropdown = signal(false);
  loadingGovernorates = signal(false);

  cities = signal<LookupVTO[]>([]);
  selectedCityId = signal<number | null>(null);
  loadingCities = signal(false);

  regions = signal<LookupVTO[]>([]);
  selectedRegionId = signal<number | null>(null);
  loadingRegions = signal(false);

  createdOnFrom = signal<string | null>(null);
  createdOnTo = signal<string | null>(null);
  lastModifiedOnFrom = signal<string | null>(null);
  lastModifiedOnTo = signal<string | null>(null);
  createdDateInvalid = signal(false);
  lastModifiedDateInvalid = signal(false);

  isArabic = computed(() => this.locale.startsWith('ar'));
  sortOptions = computed(() => [
    { value: 'CREATION_DATE', labelEn: 'Creation Date', labelAr: 'تاريخ الإنشاء' },
    { value: 'LAST_MODIFICATION_DATE', labelEn: 'Last Modified', labelAr: 'آخر تعديل' },
  ]);

  governorateOptions = computed(() =>
    this.governorates().map((g) => ({ id: String(g.id), titleAr: g.titleAr, titleEn: g.titleEn })),
  );
  cityOptions = computed(() => this.cities().map((c) => ({ id: c.id, titleAr: c.titleAr, titleEn: c.titleEn })));
  regionOptions = computed(() => this.regions().map((r) => ({ id: r.id, titleAr: r.titleAr, titleEn: r.titleEn })));

  private governorateChange$ = new Subject<number[]>();
  private cityChange$ = new Subject<number[]>();

  ngOnInit(): void {
    this.loadGovernorates();

    this.routeInstancesService.getRouteInstanceStatuses().subscribe((res) => {
      this.statusOptions.set(res.list.map((s) => ({ id: String(s.id), titleEn: s.titleEn, titleAr: s.titleAr })));
    });
    this.routeInstancesService.getRouteIntervals().subscribe((res) => {
      this.dispatchTimeOptions.set(res.list.map((i) => ({ id: i.timeFrom, titleEn: i.timeFrom, titleAr: i.timeFrom })));
    });
    this.routeInstancesService.getOrdersCounts().subscribe((res) => {
      this.ordersCountOptions.set(
        res.list.map((i: CountResult) => ({ id: i.count, titleEn: String(i.count), titleAr: String(i.count) })),
      );
    });
    this.routeInstancesService.getPharmaciesCounts().subscribe((res) => {
      this.pharmaciesCountOptions.set(
        res.list.map((i: CountResult) => ({ id: i.count, titleEn: String(i.count), titleAr: String(i.count) })),
      );
    });

    this.governorateChange$
      .pipe(
        switchMap((ids) => {
          this.loadingCities.set(true);
          return this.lookupService.getCities(ids).pipe(finalize(() => this.loadingCities.set(false)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res) => this.cities.set(res.list));

    this.cityChange$
      .pipe(
        switchMap((ids) => {
          this.loadingRegions.set(true);
          return this.lookupService.getRegions(ids).pipe(finalize(() => this.loadingRegions.set(false)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res) => this.regions.set(res.list));
  }

  loadGovernorates() {
    this.loadingGovernorates.set(true);
    this.lookupService
      .getGovernorates()
      .pipe(
        finalize(() => this.loadingGovernorates.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res) => this.governorates.set(res.list));
  }

  toggleGovernorateDropdown() {
    this.showGovernorateDropdown.set(!this.showGovernorateDropdown());
    this.showStatusDropdown.set(false);
    this.showDispatchDropdown.set(false);
  }

  onGovernorateChange(ids: string[]) {
    this.selectedGovernorateIds.set(ids);
    this.selectedCityId.set(null);
    this.selectedRegionId.set(null);
    this.cities.set([]);
    this.regions.set([]);

    if (ids.length > 0) {
      this.governorateChange$.next(ids.map(Number));
    }
  }

  onCityChange(cityId: any) {
    this.selectedCityId.set(cityId ?? null);
    this.selectedRegionId.set(null);
    this.regions.set([]);

    if (cityId) {
      this.cityChange$.next([Number(cityId)]);
    }
  }

  onToggleSortOrder() {
    this.toggleSortOrder.emit();
  }

  onDistributorSearchChange(value: string) {
    this.distributorSearchTerm.set(value);
    this.selectedDistributorId.set(null);

    if (!value) {
      this.showDistributorDropdown.set(false);
      this.distributorResults.set([]);
      return;
    }

    this.distributorLoading.set(true);
    this.showDistributorDropdown.set(true);

    this.routeInstancesService
      .getDistributorsAutoComplete(value)
      .pipe(finalize(() => this.distributorLoading.set(false)))
      .subscribe({
        next: (res) => {
          this.distributorResults.set(
            res.list.map((i: { id: number; fullName: string }) => ({
              id: i.id,
              titleAr: i.fullName,
              titleEn: i.fullName,
            })),
          );
        },
        error: () => this.distributorError.set(this.isArabic() ? 'حدث خطأ' : 'Error fetching results'),
      });
  }

  onDistributorSelect(item: { id: number; titleAr: string; titleEn: string }) {
    this.selectedDistributorId.set(item.id);
    this.distributorSearchTerm.set(item.titleEn);
    this.showDistributorDropdown.set(false);
  }

  onApplyFilters() {
    const createdInvalid = !!(
      this.createdOnFrom() &&
      this.createdOnTo() &&
      this.createdOnFrom()! > this.createdOnTo()!
    );
    const lastModInvalid = !!(
      this.lastModifiedOnFrom() &&
      this.lastModifiedOnTo() &&
      this.lastModifiedOnFrom()! > this.lastModifiedOnTo()!
    );

    this.createdDateInvalid.set(createdInvalid);
    this.lastModifiedDateInvalid.set(lastModInvalid);
    if (createdInvalid || lastModInvalid) return;

    this.applyFilters.emit({
      routeName: this.routeName() || undefined,
      distributorName: this.selectedDistributorId() ?? undefined,
      statusIds: this.selectedStatusIds().length ? this.selectedStatusIds().map(Number) : undefined,
      ordersCount: this.selectedOrdersCount() ?? undefined,
      pharmaciesCount: this.selectedPharmaciesCount() ?? undefined,
      governorateIds: this.selectedGovernorateIds().length ? this.selectedGovernorateIds().map(Number) : undefined,
      cityIds: this.selectedCityId() ? [this.selectedCityId()!] : undefined,
      regionIds: this.selectedRegionId() ? [this.selectedRegionId()!] : undefined,
      dispatchTimeId: this.selectedDispatchTimeIds().length ? this.selectedDispatchTimeIds() : undefined,
      createdOnFrom: this.createdOnFrom() ?? undefined,
      createdOnTo: this.createdOnTo() ?? undefined,
      lastModifiedOnFrom: this.lastModifiedOnFrom() ?? undefined,
      lastModifiedOnTo: this.lastModifiedOnTo() ?? undefined,
    });
  }

  onResetFilters() {
    this.routeName.set('');
    this.distributorSearchTerm.set('');
    this.selectedDistributorId.set(null);
    this.distributorResults.set([]);
    this.selectedStatusIds.set([]);
    this.selectedDispatchTimeIds.set([]);
    this.selectedOrdersCount.set(null);
    this.selectedPharmaciesCount.set(null);
    this.selectedGovernorateIds.set([]);
    this.selectedCityId.set(null);
    this.selectedRegionId.set(null);
    this.cities.set([]);
    this.regions.set([]);
    this.createdOnFrom.set(null);
    this.createdOnTo.set(null);
    this.lastModifiedOnFrom.set(null);
    this.lastModifiedOnTo.set(null);
    this.createdDateInvalid.set(false);
    this.lastModifiedDateInvalid.set(false);
    this.closeAllDropdowns();
    this.resetFilters.emit();
  }

  closeAllDropdowns() {
    this.showGovernorateDropdown.set(false);
    this.showStatusDropdown.set(false);
    this.showDispatchDropdown.set(false);
    this.showDistributorDropdown.set(false);
  }

  onContainerClick(event: Event) {
    event.stopPropagation();
  }

  toggleStatusDropdown() {
    this.showStatusDropdown.set(!this.showStatusDropdown());
    this.showGovernorateDropdown.set(false);
    this.showDispatchDropdown.set(false);
  }

  toggleDispatchDropdown() {
    this.showDispatchDropdown.set(!this.showDispatchDropdown());
    this.showGovernorateDropdown.set(false);
    this.showStatusDropdown.set(false);
  }
}
